import React from 'react'
import { FaTimes } from 'react-icons/fa'
import { useThemeAssets } from '../../../hooks/useThemeAssets'
import { useThemeFeatures } from '../../../hooks/useThemeFeatures'
import { CountdownTimer } from '../../../components/LottoGameCard'
import { useTranslation } from 'react-i18next'
import { DATE_TIME_FORMAT_HUMAN } from '../../../modules/datetime/constants'
import { formatDateTime } from '../../../modules/datetime/utils'
import { FlagBannerNameType } from '../../../types/flag.types'
import styles from './LottoBannerCard.module.css'
import { useCurrentEvent, EventType } from '../../../hooks/useCurrentEvent'
import { ThemeName } from '../../../constants/theme'

export interface LottoBannerCardProps {
  country: string // Used for small flag rendering
  flagBackgroundName: FlagBannerNameType // Used for background flag rendering
  title: string
  timeRemaining: Date | string
  roundNumber?: number | string
  isCancelled?: boolean
}

const LottoBannerCard: React.FC<LottoBannerCardProps> = ({
  country,
  flagBackgroundName,
  title,
  timeRemaining,
  roundNumber,
  isCancelled = false,
}) => {
  const [isOverdue, setIsOverdue] = React.useState<boolean>(false)
  const { t, i18n } = useTranslation()
  const {
    modelImageUrls,
    backgroundFlagImageUrls,
    smallFlagImageUrls,
    svgAssets,
    themeName,
  } = useThemeAssets()
  const themeFeatures = useThemeFeatures()
  const event = useCurrentEvent()

  // Check if Chinese New Year event is active
  const isChineseNewYear = event === EventType.CHINESE_NEW_YEAR
  // Chinese knot is available from svgAssets when event is active (blue theme only)
  const chineseKnotImage = (svgAssets as { CHINESE_KNOT?: string | null })
    ?.CHINESE_KNOT

  const date = formatDateTime(
    DATE_TIME_FORMAT_HUMAN,
    timeRemaining,
    i18n.language,
  )

  const time = formatDateTime('HH:mm', timeRemaining, i18n.language)

  const timeRemainingDate =
    typeof timeRemaining === 'string' ? new Date(timeRemaining) : timeRemaining

  let forceTextBoxDisplay = false
  let textBoxTitle = t('betResult.waiting_for_results.title')
  let textBoxMessage = t('betResult.waiting_for_results.message')
  let textBoxIcon = (
    <div className="w-7 h-7 border-4 border-t-transparent border-[var(--timer-box-text)] rounded-full animate-spin mr-2"></div>
  )
  if (isCancelled) {
    forceTextBoxDisplay = true
    textBoxTitle = t('betResult.is_cancelled.title')
    textBoxMessage = t('betResult.is_cancelled.message')
    textBoxIcon = (
      <FaTimes className="w-7 h-7 mr-2 text-[var(--timer-box-text)]" />
    )
  }

  return (
    <div
      className={`lotto-banner-card ${styles.card} relative flex flex-col md:flex-row items-stretch shadow-lg1 w-full h-full overflow-visible`}
      style={{
        backgroundImage: `url(${backgroundFlagImageUrls[flagBackgroundName]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Gradient Overlay */}
      <div
        className={`lotto-banner-card-gradient ${styles.gradientOverlay} absolute inset-0 pointer-events-none overflow-hidden lotto-card-gradient-overlay`}
      />

      {/* Model Image */}
      <div className="hidden md:flex w-1/2 justify-center relative z-0">
        <img
          src={
            themeFeatures.useYeekeeModel && flagBackgroundName === 'yeekee'
              ? modelImageUrls['yeekee']
              : modelImageUrls[country]
          }
          alt={`${title} Theme`}
          className={`${styles.modelImage} object-contain`}
        />
      </div>

      {/* Card */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 relative z-10">
        <div className="lotto-card-style rounded-xl p-8 w-full max-w-md h-[280px] text-center relative">
          {/* Chinese New Year Chinese Knot - Only shows during CNY (Blue theme only) */}
          {isChineseNewYear &&
            chineseKnotImage &&
            themeName === ThemeName.BLUE && (
              <>
                <img
                  src={chineseKnotImage}
                  alt="Chinese New Year Knot"
                  className="cny-banner-knot"
                />
                <img
                  src={chineseKnotImage}
                  alt="Chinese New Year Knot Small"
                  className="cny-banner-knot-small"
                />
              </>
            )}
          {/* Header */}
          <div className="flex items-center gap-4 mb-2">
            <h3 className="font-bold text-lg text-[var(--lotto-card-title-text)] text-left flex-1">
              {t(title, roundNumber ? { round: roundNumber } : undefined)}
            </h3>
            <img
              src={smallFlagImageUrls[country]}
              alt={`${country} flag`}
              className="w-12 h-12 rounded-full border-2 border-[var(--flag-white-border)] flex-shrink-0 object-cover"
            />
          </div>

          <div className="flex justify-between text-sm text-[var(--lotto-card-date-text)] mb-2">
            <span>{t('betResult.closed_at', { date })}</span>
            <span>{time}</span>
          </div>

          {/* Timer */}
          {timeRemaining && (
            <>
              <hr className="border-t border-[var(--lotto-card-divider-border)] my-2" />
              <div className="flex items-center justify-center h-[120px]">
                {!forceTextBoxDisplay && !isOverdue ? (
                  <div className="lotto-banner-countdown-wrapper w-full">
                    <CountdownTimer
                      datetime={timeRemainingDate}
                      setIsOverdue={setIsOverdue}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center mb-3">
                      {textBoxIcon}
                      <span className="text-[var(--lotto-card-text)] font-semibold">
                        {textBoxTitle}
                      </span>
                    </div>
                    <div className="text-sm text-[var(--lotto-card-text)]">
                      {textBoxMessage}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default LottoBannerCard
